import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cachedGet } from '../../lib/api-client';
import { Spinner } from '../../components/kit/Spinner';
import { ErrorState } from '../../components/kit/ErrorState';
import { Card } from '../../components/kit/Card';

type SlipView = {
  id: string;
  status: string;
  client?: { name: string } | null;
  startKm?: number | null;
  endKm?: number | null;
  passengerSignature?: string | null;
  requesterRating?: number | null;
  notes?: string | null;
  dutySlipUrl?: string | null;
  expenses?: Array<{ id: string; type: string; amount: number; description?: string | null }>;
};

export function DutySlipViewRoute() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation('trip');
  const [trip, setTrip] = useState<SlipView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await cachedGet<SlipView>(`/driver-app/me/trips/${id}`);
        if (!cancelled) setTrip(res.data);
      } catch (e: unknown) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  if (loading) return <div className="flex justify-center p-8"><Spinner size={32} /></div>;
  if (error || !trip) return <ErrorState />;

  const stars = '★'.repeat(trip.requesterRating ?? 0) + '☆'.repeat(5 - (trip.requesterRating ?? 0));

  return (
    <div className="p-4 space-y-4 pb-8">
      <h1 className="text-2xl font-extrabold text-[var(--color-text)]" style={{ letterSpacing: '-0.025em' }}>
        {t('slip_view_anchor', { defaultValue: 'Submitted Duty' })}{' '}
        <span className="serif-accent">{t('slip_view_verb', { defaultValue: 'Slip' })}</span>
      </h1>
      <Card>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>
            <div className="text-[var(--color-text-secondary)]">{t('client', { defaultValue: 'Client' })}</div>
            <div className="font-medium text-[var(--color-text)]">{trip.client?.name ?? '—'}</div>
          </div>
          <div>
            <div className="text-[var(--color-text-secondary)]">{t('km_summary', { defaultValue: 'KM' })}</div>
            <div className="font-medium text-[var(--color-text)]">
              {trip.startKm ?? '—'} → {trip.endKm ?? '—'}
            </div>
          </div>
        </div>
      </Card>

      {trip.passengerSignature && (
        <Card>
          <div className="text-sm font-medium text-[var(--color-text)] mb-2">{t('customer_signature', { defaultValue: 'Customer signature' })}</div>
          <img src={trip.passengerSignature} alt="" className="w-full bg-white rounded-xl border border-[var(--color-border,#d1d5db)]" />
        </Card>
      )}

      {trip.requesterRating != null && (
        <Card>
          <div className="text-sm font-medium text-[var(--color-text)] text-center mb-2">{t('customer_rating', { defaultValue: 'Customer rating' })}</div>
          <div className="text-3xl text-[#F59E0B] text-center tracking-widest">{stars}</div>
        </Card>
      )}

      {trip.notes && (
        <Card>
          <div className="text-sm font-medium text-[var(--color-text)] mb-2">{t('notes_optional', { defaultValue: 'Notes' })}</div>
          <p className="text-sm text-[var(--color-text-secondary)] whitespace-pre-wrap">{trip.notes}</p>
        </Card>
      )}

      {trip.dutySlipUrl && (
        <Card>
          <div className="text-sm font-medium text-[var(--color-text)] mb-2">{t('slip_photo_optional', { defaultValue: 'Duty slip photo' })}</div>
          <img src={trip.dutySlipUrl} alt="" className="w-full rounded-xl border border-[var(--color-border,#d1d5db)]" />
        </Card>
      )}

      {trip.expenses && trip.expenses.length > 0 && (
        <Card>
          <div className="text-sm font-medium text-[var(--color-text)] mb-2">{t('expenses', { defaultValue: 'Expenses' })}</div>
          <ul className="text-sm space-y-1">
            {trip.expenses.map(e => (
              <li key={e.id} className="flex justify-between text-[var(--color-text-secondary)]">
                <span>{e.type}{e.description ? ' — ' + e.description : ''}</span>
                <span className="font-mono">₹ {e.amount.toFixed(0)}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Link to={`/trips/${id}`} className="block text-center text-sm text-[var(--green-700,#1B6A41)] underline pt-2">
        {t('back_to_trip', { defaultValue: 'Back to trip details' })}
      </Link>
    </div>
  );
}
